import { Paper, Stack, Typography } from "@mui/material";
import { keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import {
  type RowClickFunction,
  Datagrid,
  List,
  TextField,
  TopToolbar,
  WrapperField,
  ShowButton,
  EmailField,
  useGetList,
  CreateButton,
  usePaginationState,
  Pagination,
  ListPaginationContext,
  DeleteWithConfirmButton,
} from "react-admin";

type User = {
  id: number;
  groups: { id: number }[];
};

type Group = {
  id: number;
  name: string;
  users: User[];
};

import { FILTER_LIMITS } from "@/constants/filters";

const ListActions = () => <TopToolbar></TopToolbar>;

export const GroupsList = () => {
  const userPaginationState = usePaginationState({
    page: 1,
    perPage: FILTER_LIMITS[0],
  });
  const userList = useGetList<User>(
    "users",
    {
      pagination: {
        page: userPaginationState.page,
        perPage: userPaginationState.perPage,
      },
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  const handleRowClick: RowClickFunction = (_index, _resource, record) => {
    setSelectedGroup(record as unknown as Group);

    return false;
  };

  const userListData: User[] = (userList.data ?? []).filter(
    (user) =>
      !selectedGroup ||
      user.groups.some((group) => group.id === selectedGroup.id),
  );

  return (
    <List actions={<ListActions />} pagination={false} storeKey={false}>
      <Stack
        direction={{
          xs: "column",
          xl: "row",
        }}
        padding={{
          xs: 2,
          xl: 4,
        }}
        spacing={6}
        justifyContent="stretch"
      >
        <Paper
          sx={{
            flex: 1,
            padding: 2,
          }}
          elevation={1}
          variant="outlined"
        >
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Typography variant="h1" sx={{ fontSize: 24 }}>
              Groups
            </Typography>
            <CreateButton label="Create group" resource="groups" />
          </Stack>

          <Datagrid
            sx={{
              marginTop: 2,
            }}
            bulkActionButtons={false}
            rowClick={handleRowClick}
          >
            <TextField source="name" sortable={false} />
            <TextField source="description" sortable={false} />
            <WrapperField sortable={false} textAlign="right">
              <ShowButton />
              <DeleteWithConfirmButton />
            </WrapperField>
          </Datagrid>
        </Paper>
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            padding: 2,
          }}
          elevation={1}
          variant="outlined"
        >
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Typography variant="h1" sx={{ fontSize: 24 }}>
              {selectedGroup ? `"${selectedGroup.name}" group users` : "Users"}
            </Typography>
            <CreateButton label="Create user" resource="users" />
          </Stack>

          <Stack
            sx={{ flex: 1 }}
            direction="column"
            justifyContent="space-between"
          >
            <Datagrid
              resource="users"
              sx={{
                marginTop: 2,
              }}
              bulkActionButtons={false}
              data={userListData}
            >
              <TextField source="id" sortable={false} />
              <TextField source="username" sortable={false} />
              <EmailField source="email" sortable={false} />
            </Datagrid>
            <ListPaginationContext.Provider
              value={{
                resource: "users",
                isPending: userList.isPending,
                isLoading: userList.isLoading,
                total: userList.total,
                ...userPaginationState,
              }}
            >
              <Pagination rowsPerPageOptions={FILTER_LIMITS} />
            </ListPaginationContext.Provider>
          </Stack>
        </Paper>
      </Stack>
    </List>
  );
};
